import { QK } from '../../../consts/query-keys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import {
    createCoachingForm,
    deleteCoachingForm,
    getCoachingForm,
    getCoachingFormProtocolsUsage,
    getCoachingFormTable,
    updateCoachingForm,
    updateCoachingFormInactiveState,
} from './coachingFormApi';
import { Ref } from 'vue';
import { RequestPostCoachingForm, RequestPostCoachingFormRevision } from './types';

export const useGetCoachingForms = (includeInactive?: Ref<boolean>) => {
    return useQuery({
        queryKey: [QK.COACHING_FORMS.GET, includeInactive],
        queryFn: async () =>
            getCoachingFormTable({ includeInactive: includeInactive?.value ?? false }),
    });
};

export const useGetCoachingForm = (id: Ref<string | null>) => {
    return useQuery({
        queryKey: [QK.COACHING_FORM.GET, id.value],
        queryFn: async () => await getCoachingForm({ id: id.value ?? '' }),
        enabled: !!id.value,
    });
};

export const useMutateDeleteCoachingForm = (id: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: [QK.COACHING_FORM.DELETE, id],
        mutationFn: (force: boolean = false) => deleteCoachingForm({ id }, { force }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QK.COACHING_FORMS.GET] });
        },
    });
};

export const useMutateCreateCoachingForm = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: [QK.COACHING_FORM.CREATE],
        mutationFn: (request: RequestPostCoachingForm) => createCoachingForm(request),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QK.COACHING_FORMS.GET],
            });
        },
    });
};

export const useGetCoachingFormProtocolsUsage = (id: string) => {
    return useQuery({
        queryKey: [QK.COACHING_FORM.PROTOCOLS_USAGE, id],
        queryFn: async () => await getCoachingFormProtocolsUsage({ id }),
    });
};

export const useMutateUpdateCoachingForm = (formId: Ref<string | null>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (request: RequestPostCoachingFormRevision) => updateCoachingForm(request),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QK.COACHING_FORMS.GET],
            });

            queryClient.invalidateQueries({
                queryKey: [QK.COACHING_FORM.GET, formId.value],
            });
        },
    });
};

export const useMutateUpdateCoachingFormInactiveState = (formId: Ref<string | null>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (isActive: boolean) =>
            updateCoachingFormInactiveState({ id: formId.value ?? '', isInactive: !isActive }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QK.COACHING_FORMS.GET],
            });

            queryClient.invalidateQueries({
                queryKey: [QK.COACHING_FORM.GET, formId.value],
            });
        },
    });
};

// TODO: Delete if not used
export const useMutateCopyCoachingForm = (id: string, title: string) => {
    return useMutation({
        mutationKey: [QK.COACHING_FORM.COPY, id, title],
        mutationFn: async () => {
            return new Promise((resolve) => {
                window.setTimeout(() => {
                    resolve(true);
                }, 1000);
            });
        },
    });
};
